import React, {
  useEffect,
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
} from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Grid2,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Fab,
  Skeleton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Edit from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import { LoadingButton } from "@mui/lab";
import { getPatients, searchPatients } from "../API/PatientsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import authContext from "../Context/AuthProvider";
import { AddRounded, SearchRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import NewPatient from "./NewPatient";
import { capitalizeFirstLetter, formatAge } from "../Utility/Utility";
import duration from "dayjs/plugin/duration";
import PatientSearchDialog from "./PatientSearchDialog";

const PatientList = forwardRef(
  ({ onViewDetails, onNewPatient, onEditPatient }, ref) => {
    const [patients, setPatients] = useState([]);
    const { showSnackbar } = useSnackbar();
    const { auth } = useContext(authContext);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showSearchPanel, setShowSearchPanel] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
      const loadPatients = async () => {
        setShowSpinner(true);
        const result = await getPatients(auth);
        if (result.isSuccess) {
          setPatients(result.data);
        } else {
          showSnackbar("Failed to load patients", "error");
        }
        setShowSpinner(false);
      };

      loadPatients();
    }, [auth]);

    // const searchPatient = async (e) => {
    //   e.preventDefault();

    //   if (searchKeyword.trim().length < 3) {
    //     showSnackbar(
    //       "Enter at least 3 characters to search. Special characters are not allowed.",
    //       "error"
    //     );
    //   } else {
    //     try {
    //       const result = await searchPatients(auth, searchKeyword);
    //       if (result?.isSuccess) {
    //         setSelectedPatientId(null);
    //         setSearchResult(result?.data);
    //       } else {
    //         setSearchResult([]);
    //       }
    //     } catch (error) {
    //       console.error("Error searching patients:", error);
    //       setSearchResult([]);
    //     }
    //   }
    // };

    const refreshPatientList = async () => {
      setShowSpinner(true);
      const result = await getPatients(auth);
      if (result.isSuccess) {
        setPatients(result.data);
      } else {
        showSnackbar("Failed to load patients", "error");
      }
      setShowSpinner(false);
    };

    useImperativeHandle(ref, () => ({
      refreshPatientList,
    }));

    const patientColumns = [
      { field: "mrn", headerName: "MRN", width: 200 },
      {
        field: "patient",
        type: "text",
        headerName: "Patient",
        width: 180,
        renderCell: (params) =>
          `${capitalizeFirstLetter(
            params?.row?.firstName
          )} ${capitalizeFirstLetter(params?.row?.lastName)}`,
      },
      { field: "gender", headerName: "Gender", width: 100 },
      {
        field: "ageInYears",
        headerName: "Age",
        width: 160,
        renderCell: (params) => `${formatAge(params?.row?.ageInYears)}`,
      },
      { field: "mobileNo", headerName: "Phone No.", width: 150 },
      { field: "emailId", headerName: "Email Id", width: 200 },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        headerClassName: "sticky-column",
        cellClassName: "sticky-column",
        renderCell: (params) => {
          if (!params || !params.row) return null;
          return (
            <>
              <Tooltip title="View Details" arrow>
                <IconButton onClick={() => onViewDetails(params.row)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Patient" arrow>
                <IconButton onClick={() => onEditPatient(params.row)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    ];

    const renderPatientCards = () => (
      <Card sx={{ px: 1 }}>
        {patients.map((patient) => (
          <Card
            key={patient.id}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight="bold">
                {capitalizeFirstLetter(patient.firstName)}{" "}
                {capitalizeFirstLetter(patient.lastName)}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" gutterBottom>
                MRN: {patient.mrn}
              </Typography>
              <Typography variant="body2">Gender: {patient.gender}</Typography>
              <Typography variant="body2">
                Age: {formatAge(patient.ageInYears)}
              </Typography>
              <Typography variant="body2">Phone: {patient.mobileNo}</Typography>
              <Typography variant="body2">Email: {patient.emailId}</Typography>
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => onViewDetails(patient)}
              >
                View
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<Edit />}
                onClick={() => onEditPatient(patient)}
              >
                Edit
              </Button>
            </CardActions>
          </Card>
        ))}
      </Card>
    );

    return (
      <>
        <Grid2 container sx={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}>
          {/* Fixed Header Section */}
          <Grid2
            container
            size={12}
            alignItems="center"
            justifyContent="space-between"
            spacing={0}
            sx={{ px: 1, py: 1 }}
          >
            {/* Left Side: Heading */}
            <Grid2
              size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              sx={{ pl: 0 }}
            >
              <Typography variant="body1" sx={{ ml: 0 }}>
                Recent Patients (Upto 100)
              </Typography>
            </Grid2>

            {/* Right Side: Buttons */}
            <Grid2
              size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              display="flex"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              alignItems="center"
              gap={1}
              sx={{ pr: 0 }}
            >
              <Tooltip title="New Patient" arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  size="medium"
                  onClick={onNewPatient}
                  startIcon={<AddRounded fontSize="small" />}
                >
                  New Patient
                </Button>
              </Tooltip>

              <Tooltip title="Search Patient" arrow>
                <Fab
                  color="secondary"
                  size="small"
                  onClick={() => setShowSearchPanel(true)}
                >
                  <SearchRounded />
                </Fab>
              </Tooltip>
            </Grid2>
          </Grid2>

          <Card sx={{ width: "100%", paddingX: "5px", boxShadow: "none" }}>
            {isMobile ? (
              renderPatientCards()
            ) : (
              <DataGrid
                localeText={{
                  noRowsLabel: "There are no patients to display.",
                }}
                loading={showSpinner}
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
                density="compact"
                rows={patients}
                columns={patientColumns}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                disableColumnMenu
                sx={{
                  width: "100%",
                  "& .sticky-column": {
                    position: "sticky",
                    right: 0, // Keep it on the right side
                    backgroundColor: "white", // To make sure it has a solid background
                    zIndex: 100, // Ensure it stays on top of other columns
                  },
                }}
              />
            )}
          </Card>
        </Grid2>

        <PatientSearchDialog
          key="fromPatientList"
          setShowSearchPanel={setShowSearchPanel}
          showSearchPanel={showSearchPanel}
          onViewDetails={onViewDetails}
        />
      </>
    );
  }
);

export default PatientList;
